import {
  CALLOUT_BLOCK_MARKER_CLASS,
  CALLOUT_DENSITY_ICON_SIZE,
  CALLOUT_DENSITY_TYPOGRAPHY,
  calloutVariantToDensity,
  getCalloutBulletComponents,
  getCalloutDensity,
  mermaidConfig,
  reconstructNestedMarkdown,
  shikiTheme,
  streamdownPlugins,
  streamdownRehypePlugins,
  type CalloutDensity,
  type CalloutVariant,
} from '@heidi/answer-kit';
import { typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { useMemo, type ComponentType, type JSX, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Streamdown } from 'streamdown';
import {
  countTopLevelListItems,
  escapeLeadingBlockMarkdown,
  partitionKeySafetyNotesContent,
  stripCaveatWarningPrefix,
  stripDecorativeIcons,
} from './formatKeySafetyNotesMarkdown';

interface KeySafetyNotesProps {
  children?: ReactNode;
  className?: string;
  isStreaming?: boolean;
  /** `<cite-badge>` renderer supplied by the surface that owns the citation map. */
  citeBadgeComponent?: ComponentType<{ 'data-idx'?: string }>;
  /**
   * The surface hosting the block. Sizes the certified block; the muted block
   * only uses it to derive a default density, and that is only correct where
   * the variant tracks the prose size — prefer `density` there.
   */
  variant?: CalloutVariant;
  /**
   * The hosting surface's type density, which sets every size in the muted
   * block. Pass this whenever the surface knows its own prose size; `variant`
   * is a guess and gets it wrong wherever "no scribe session" does not mean
   * "16px prose" (Projects being the standing example).
   */
  density?: CalloutDensity;
  /**
   * Opt into the muted block: neutral chrome, density-driven type, and
   * model-emitted icons stripped. Off by default — the certified amber block
   * with its warning icons is what UK/EU/SE_AZ1 must see, so callers only pass
   * this once `shouldMuteKeySafetyNotes` has cleared the account's region.
   */
  muted?: boolean;
}

type KeySafetyNotesRendererProps = Omit<KeySafetyNotesProps, 'muted'> & {
  title: ReactNode;
};

/**
 * The certified block: amber chrome, `ShieldAlert` / `AlertTriangle` icons and
 * the model's content as emitted. Sizing follows `variant`.
 *
 * Bullet typography is shared with `KeyPointsBlock` via
 * `getCalloutBulletComponents` — see `callout-helpers.tsx`. The only
 * intentional divergences from KeyPoints are: amber chrome (border + bg),
 * `ShieldAlert` / `AlertTriangle` icons, the caveat sub-list, and the
 * `key_safety_notes` cite-badge surface.
 */
const CertifiedKeySafetyNotes = ({
  title,
  children,
  className,
  isStreaming = false,
  citeBadgeComponent,
  variant = 'in-session',
}: KeySafetyNotesRendererProps): JSX.Element | null => {
  const isOutOfSession = variant === 'out-of-session';

  const { citedMarkdown, caveatLines } = useMemo(() => {
    const raw = reconstructNestedMarkdown(children);
    return partitionKeySafetyNotesContent(raw);
  }, [children]);

  const components = useMemo(
    () =>
      getCalloutBulletComponents({
        density: calloutVariantToDensity(variant),
        citeBadgeComponent,
      }),
    [citeBadgeComponent, variant]
  );

  if (!citedMarkdown && caveatLines.length === 0) return null;

  return (
    <div
      data-testid="key-safety-notes"
      className={cn(
        // Shared marker class — see callout-helpers.ts.
        CALLOUT_BLOCK_MARKER_CLASS,
        'flex flex-col rounded-xl border border-validation-warning-border bg-validation-warning-muted',
        // Mirror KeyPointsBlock's variant sizing so both callouts breathe
        // identically when stacked in the same response.
        isOutOfSession ? 'gap-4 p-6' : 'gap-3 px-4 py-5',
        'my-4 first:mt-0',
        'min-w-0 overflow-hidden',
        className
      )}
    >
      <div className="flex items-center gap-1">
        <ShieldAlert
          size={isOutOfSession ? 20 : 18}
          className="text-validation-warning"
        />
        <span
          className={cn(
            'font-exposure font-medium tracking-tight text-text-primary',
            isOutOfSession ? 'text-xl' : 'text-base'
          )}
        >
          {title}
        </span>
      </div>

      {/* Accent ink wrapper — see KeyPointsBlock for the rationale. Color
          cascades to <li> / <p> / <strong> via inheritance so the shared
          factory stays color-agnostic. */}
      <div className="text-text-primary">
        {citedMarkdown ? (
          <Streamdown
            components={components}
            isAnimating={isStreaming}
            codeBlockMaxHeight={0}
            tableMaxHeight={0}
            plugins={streamdownPlugins}
            mermaid={mermaidConfig}
            shikiTheme={shikiTheme}
            rehypePlugins={streamdownRehypePlugins}
          >
            {citedMarkdown}
          </Streamdown>
        ) : null}

        {caveatLines.length > 0 ? (
          <ul
            className={cn(
              // Caveat sub-list (the `⚠`-prefixed lines). Same indent as
              // cited bullets so the two read as one column; triangle icon
              // sits in the marker column. Variant-aware sizing matches the
              // cited bullets above.
              'list-none space-y-2 font-normal leading-normal text-validation-warning',
              isOutOfSession ? 'ps-6 text-base' : 'ps-5 text-sm',
              citedMarkdown && 'mt-2'
            )}
            data-testid="key-safety-notes-caveats"
          >
            {caveatLines.map((line) => (
              <li key={line} className="relative">
                <AlertTriangle
                  size={14}
                  aria-hidden
                  className="absolute -start-4 top-1 size-3.5 shrink-0 text-validation-warning"
                />
                {/* Caveat text is rendered through the same cite-badge-aware
                    Streamdown as the cited bullets (EVI-1803). Rendering it as
                    a raw string left any inline `<cite-badge>` marker visible
                    as literal text. The shared `p` handler maps to a <span>,
                    so the caveat stays inline next to the triangle marker.
                    `escapeLeadingBlockMarkdown` neutralizes a leading block
                    trigger (e.g. `1.`, `-`, `>`, `#`) so Streamdown can't turn
                    the caveat into a nested list/heading/blockquote block,
                    which would break the hanging triangle layout. */}
                <Streamdown
                  components={components}
                  isAnimating={isStreaming}
                  codeBlockMaxHeight={0}
                  tableMaxHeight={0}
                  plugins={streamdownPlugins}
                  mermaid={mermaidConfig}
                  shikiTheme={shikiTheme}
                  rehypePlugins={streamdownRehypePlugins}
                >
                  {escapeLeadingBlockMarkdown(stripCaveatWarningPrefix(line))}
                </Streamdown>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

/**
 * The muted Key Safety Notes block, shown only where `muted` is passed.
 *
 * Cited safety bullets and conditional caveats both render as plain bullets via
 * nested Streamdown. The block carries exactly one icon — `ShieldAlert` in the
 * header — and any pictographic character the model emits inside the prose is
 * stripped before render (see `stripDecorativeIcons`).
 *
 * Every size in the block comes off the surface's `CalloutDensity` — compact
 * (in-session) is p2/14px and comfortable (out-of-session) is p1/16px, header
 * and body alike — so the card always reads at the size of the prose around it.
 *
 * Bullet typography is shared with `KeyPointsBlock` via
 * `getCalloutBulletComponents` — see `callout-helpers.tsx`. The only
 * intentional divergences from KeyPoints are: neutral tertiary chrome
 * (border + bg), the `ShieldAlert` header icon, the caveat sub-list, and the
 * `key_safety_notes` cite-badge surface.
 */
const MutedKeySafetyNotes = ({
  title,
  children,
  className,
  isStreaming = false,
  citeBadgeComponent,
  variant = 'in-session',
  density: densityProp,
}: KeySafetyNotesRendererProps): JSX.Element | null => {
  const density = densityProp ?? getCalloutDensity(variant);
  const isComfortable = density === 'comfortable';

  const { citedMarkdown, caveatLines } = useMemo(() => {
    const raw = reconstructNestedMarkdown(children);
    const partitioned = partitionKeySafetyNotesContent(raw);
    // Stripping happens after partitioning: `⚠` is how a caveat is recognised,
    // and it is itself one of the glyphs removed here.
    return {
      citedMarkdown: stripDecorativeIcons(partitioned.citedMarkdown),
      caveatLines: partitioned.caveatLines
        .map((line) => stripDecorativeIcons(stripCaveatWarningPrefix(line)))
        .filter(Boolean),
    };
  }, [children]);

  // A list of one is not a list: when the whole block comes to a single point,
  // the disc and its hanging indent are dropped and the point reads as the
  // sentence it is.
  const isSingleItem = countTopLevelListItems(citedMarkdown) + caveatLines.length <= 1;
  const listStyle = isSingleItem ? 'none' : 'disc';

  const components = useMemo(
    () => getCalloutBulletComponents({ density, citeBadgeComponent, listStyle }),
    [citeBadgeComponent, density, listStyle]
  );

  if (!citedMarkdown && caveatLines.length === 0) return null;

  return (
    <div
      data-testid="key-safety-notes"
      className={cn(
        // Shared marker class — see callout-helpers.ts.
        CALLOUT_BLOCK_MARKER_CLASS,
        // Neutral tertiary chrome. The amber `validation-warning` pair read as
        // an alert competing with the answer it summarises; the safety framing
        // is carried by the header and the copy, not by the surface.
        'flex flex-col rounded-xl border border-border bg-background-tertiary',
        // Padding is uniform on all four sides and set by density. Tighter than
        // KeyPoints, which keeps the taller 24px/20px inset: this block is a
        // trailing summary, and the extra inset made it read as the heavier of
        // the two cards when they stack.
        isComfortable ? 'gap-4 p-5' : 'gap-3 p-3',
        'my-4 first:mt-0',
        'min-w-0 overflow-hidden',
        className
      )}
    >
      <div className="flex items-center gap-1">
        {/* Sized off the same density row as the label, so the glyph reads as
            part of it rather than as a badge in front of it. */}
        <ShieldAlert
          size={CALLOUT_DENSITY_ICON_SIZE[density]}
          className="shrink-0 text-text-primary"
        />
        {/* The density row's header token — `pBold` at 16px, `p2Bold` at 14px.
            Both are the design system's `font-medium` step: this is a label
            introducing prose, not a heading, and Exposure stays reserved for
            KeyPoints. */}
        <span
          className={cn(
            typographyVariant({ variant: CALLOUT_DENSITY_TYPOGRAPHY[density].header }),
            'text-text-primary'
          )}
        >
          {title}
        </span>
      </div>

      {/* Ink wrapper — see KeyPointsBlock for the rationale. Color cascades to
          <li> / <p> / <strong> via inheritance so the shared factory stays
          color-agnostic. Body copy is secondary throughout: the header is the
          only element in the block that carries primary ink. */}
      <div className="text-text-secondary">
        {citedMarkdown ? (
          <Streamdown
            components={components}
            isAnimating={isStreaming}
            codeBlockMaxHeight={0}
            tableMaxHeight={0}
            plugins={streamdownPlugins}
            mermaid={mermaidConfig}
            shikiTheme={shikiTheme}
            rehypePlugins={streamdownRehypePlugins}
          >
            {citedMarkdown}
          </Streamdown>
        ) : null}

        {caveatLines.length > 0 ? (
          <ul
            className={cn(
              // Caveat sub-list (the lines the model marked with `⚠`). Same
              // marker, indent and density row as the cited bullets above, so
              // the two read as one continuous list rather than a second,
              // louder tier.
              'space-y-2',
              typographyVariant({ variant: CALLOUT_DENSITY_TYPOGRAPHY[density].body }),
              isSingleItem
                ? 'list-none ps-0'
                : isComfortable
                  ? 'list-disc ps-6'
                  : 'list-disc ps-5',
              citedMarkdown && 'mt-2'
            )}
            data-testid="key-safety-notes-caveats"
          >
            {caveatLines.map((line) => (
              <li key={line}>
                {/* Caveat text is rendered through the same cite-badge-aware
                    Streamdown as the cited bullets (EVI-1803). Rendering it as
                    a raw string left any inline `<cite-badge>` marker visible
                    as literal text. The shared `p` handler maps to a <span>,
                    so the caveat stays inline inside its bullet.
                    `escapeLeadingBlockMarkdown` neutralizes a leading block
                    trigger (e.g. `1.`, `-`, `>`, `#`) so Streamdown can't turn
                    the caveat into a nested list/heading/blockquote block. */}
                <Streamdown
                  components={components}
                  isAnimating={isStreaming}
                  codeBlockMaxHeight={0}
                  tableMaxHeight={0}
                  plugins={streamdownPlugins}
                  mermaid={mermaidConfig}
                  shikiTheme={shikiTheme}
                  rehypePlugins={streamdownRehypePlugins}
                >
                  {escapeLeadingBlockMarkdown(line)}
                </Streamdown>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

/**
 * KeySafetyNotes — the trailing "Key Safety Notes" summary block for Evidence
 * answers (EVI-1627), the safety-focused mirror of `<keypoints>`.
 *
 * Renders the certified amber block unless `muted` is passed, in which case it
 * renders `MutedKeySafetyNotes`.
 */
export const KeySafetyNotes = ({ muted = false, ...props }: KeySafetyNotesProps) => {
  const title = (
    <FormattedMessage
      defaultMessage="Key Safety Notes"
      id="EvECYARnHz"
      description="Heading for the trailing safety-notes summary on Evidence answers"
    />
  );
  return muted ? (
    <MutedKeySafetyNotes title={title} {...props} />
  ) : (
    <CertifiedKeySafetyNotes title={title} {...props} />
  );
};

export default KeySafetyNotes;
